import { useMemo, useCallback, useState, useEffect } from 'react';
import { basedAxios } from '../../../store/Store';
import { formatDateRange } from '../../../utils/date.utils';

export default function useBooking({
  order,
  roomSizes,
  onSnackbar,
  roomCount,
  startDate,
  endDate,
  hotels,
  hotelAllotments,
  hotelOrderRows,
  updateOrderRow,
  eventId,
  validateBooking,
  breakouts,
  lang = 'swe',
}) {
  const [fullbooked, setFullBooked] = useState([]);

  const createNewOrderRowId = useCallback(() => {
    const nonCreatedOrderRows = order.orderRow?.filter(row => row.orderRowId < 0)?.map(row => row.orderRowId) ?? [];

    if (!nonCreatedOrderRows.length) return -1;

    const newOrderRowId = (Math.min(...nonCreatedOrderRows) ?? 0) - 1;
    return newOrderRowId;
  }, [hotelOrderRows, order]);

  const createNewHotelGroupId = useCallback(() => {
    if (!order?.orderRow?.filter(or => or.hotelGroupId)?.length) return 1;

    const hotelGroupIds = order?.orderRow?.filter(or => or.hotelGroupId)?.map(row => row.hotelGroupId) || [];
    const newHotelGroupId = Math.max(...hotelGroupIds) + 1;
    return newHotelGroupId;
  }, [hotelOrderRows, order]);

  const handleAdd = useCallback(
    async (hotelPricings, quantity = 1) => {
      const { data } = await basedAxios.get(
        `./hotel/CheckAllotmentStatus/${eventId}?roomSizes=${encodeURIComponent(
          JSON.stringify(roomSizes.filter(size => size[1]).map(size => size[0]))
        )}&pricingIds=${encodeURIComponent(JSON.stringify(hotelPricings))}
        &start=${new Date(startDate)?.toISOString()}&end=${new Date(endDate)?.toISOString()}`
      );

      if (!data.available) {
        return setFullBooked(prev => [...prev, ...hotelPricings]);
      }

      const newQuantity = data.availableCount > quantity ? quantity : data.availableCount;

      if (data.availableCount <= quantity) {
        setFullBooked(prev => [...prev, ...hotelPricings]);
      }

      for (let index = 0; index < newQuantity; index++) {
        const hotelGroupId = createNewHotelGroupId();

        const allRooms = order.orderRow.reduce((acc, { rooms = [] }) => [...acc, ...(rooms ?? [])], []);
        let orderRowId = createNewOrderRowId();

        const allotment = hotelAllotments.find(ha => ha.hotelAllotmentPricing?.some(hap => hotelPricings?.includes(hap.allotmentPricingId)));

        if (allotment?.supplementId && breakouts?.some(b => b.eventId === allotment.supplementId)) {
          const allotmentSize = allotment?.hotelAllotmentPricing?.find(hap => hotelPricings?.includes(hap.allotmentPricingId))?.roomSize;
          const supplement = breakouts?.find(b => b.eventId === allotment.supplementId);
          const previousCount = order.orderRow.find(or => or.eventId === supplement.eventId)?.quantity ?? 0;

          updateOrderRow(
            {
              orderRowId,
              amount: supplement.amount ?? 0,
              comment: '',
              discount: 0,
              eventId: supplement.eventId,
              orderId: order?.orderId ?? -1,
              quantity: previousCount + hotelPricings?.length * allotmentSize ?? 0,
              status: null,
              vatPercent: supplement.vatPercent || 0,
              deleted: false,
              groupId: null,
              hotelAllotmentPricingId: null,
              hotelGroupId: null,
              comment: '',
            },
            false,
            order?.orderId
          );
          orderRowId--;
        }

        hotelPricings.map((pricingId, i) => {
          const allotment = hotelAllotments.find(ha => ha.hotelAllotmentPricing?.some(hap => hap.allotmentPricingId === pricingId));

          const pricing = allotment.hotelAllotmentPricing.find(hap => hap.allotmentPricingId === pricingId);

          const rooms = [];

          if (!allRooms.some(({ orderId }) => order.orderId === orderId)) {
            const roomId = ((allRooms.filter(({ roomId }) => roomId < 0)?.length ?? 0) + 1) * -1;
            rooms.push({
              roomId,
              orderId: order.orderId,
              orderRowId,
              external: false,
              roomMate: `${order?.person?.firstName} ${order?.person?.lastName}`,
              eventId,
            });
          }

          updateOrderRow(
            {
              orderRowId,
              amount: pricing.price ?? 0,
              comment: '',
              discount: 0,
              eventId: null,
              orderId: order?.orderId ?? -1,
              quantity: 1,
              status: null,
              vatPercent: pricing.vatPercent || 0,
              deleted: false,
              groupId: null,
              hotelAllotmentPricingId: pricingId,
              hotelGroupId,
              comment: '',
              rooms,
            },
            false,
            order?.orderId
          );

          orderRowId--;
        });
      }

      validateBooking();
      onSnackbar('BookingAdded');
    },
    [
      roomCount,
      roomSizes,
      order,
      hotelAllotments,
      updateOrderRow,
      onSnackbar,
      createNewHotelGroupId,
      createNewOrderRowId,
      validateBooking,
      hotelOrderRows,
      startDate,
      endDate,
      eventId,
    ]
  );

  const handleDelete = useCallback(
    hotelGroupId => {
      // Handle deletion of supplement
      const firstAllotmentPricingId = order?.orderRow?.find(or => or.hotelGroupId === hotelGroupId)?.hotelAllotmentPricingId;
      const allotment = hotelAllotments.find(ha => ha.hotelAllotmentPricing?.some(hap => hap.allotmentPricingId === firstAllotmentPricingId));
      const hotelPricings = order?.orderRow?.filter(or => or.hotelGroupId === hotelGroupId)?.map(or => or.hotelAllotmentPricingId);

      if (allotment && allotment.supplementId && breakouts?.some(b => b.eventId === allotment.supplementId)) {
        const allotmentSize = allotment?.hotelAllotmentPricing?.find(hap => hotelPricings?.includes(hap.allotmentPricingId))?.roomSize;
        const previousSupplementCount = order.orderRow.find(or => or.eventId === allotment.supplementId)?.quantity ?? 0;
        const supplementOrderRow = order.orderRow.find(o => o.eventId === allotment.supplementId);

        // If all the allotments are deleted, delete the supplement completely
        if (previousSupplementCount === hotelOrderRows?.filter(or => or.hotelGroupId === hotelGroupId)?.length * allotmentSize && supplementOrderRow) {
          updateOrderRow(
            {
              ...supplementOrderRow,
              deleted: true,
            },
            false,
            order?.orderId
          );
        } else if (supplementOrderRow) {
          // If not all the allotments are deleted, decrease the quantity of the supplement
          const newQuantity = previousSupplementCount - order?.orderRow?.filter(or => or.hotelGroupId === hotelGroupId)?.length * allotmentSize ?? 0;
          updateOrderRow(
            {
              ...supplementOrderRow,
              quantity: newQuantity,
            },
            false,
            order?.orderId
          );
        }
      }

      hotelOrderRows
        ?.filter(or => or.hotelGroupId === hotelGroupId)
        ?.forEach(or => {
          updateOrderRow(
            {
              ...or,
              deleted: true,
            },
            false,
            order?.orderId
          );
        });

      onSnackbar('RoomDeleted');
    },
    [hotelOrderRows, order, updateOrderRow, onSnackbar, hotelAllotments, breakouts]
  );

  const handleChange = useCallback(
    (value, hotelGroupId) => {
      hotelOrderRows
        ?.filter(or => or.hotelGroupId === hotelGroupId)
        ?.forEach(or => {
          updateOrderRow(
            {
              ...or,
              ...value,
            },
            false,
            order?.orderId
          );
        });
    },
    [hotelOrderRows, order]
  );

  const bookings = useMemo(() => {
    if (!hotelAllotments?.length || !hotelOrderRows?.length) return [];

    const result = hotelOrderRows
      .reduce((acc, orderRow) => {
        const hotelAllotment = hotelAllotments.find(ha => ha.hotelAllotmentPricing?.some(hap => hap.allotmentPricingId === orderRow.hotelAllotmentPricingId));

        if (!hotelAllotment) return acc;

        const hotel = hotels.find(h => h.hotelRoomType.some(y => y.hotelRoomTypeId == hotelAllotment.hotelRoomTypeId));
        const roomType = hotel.hotelRoomType.find(hr => hr.hotelRoomTypeId == hotelAllotment.hotelRoomTypeId);
        const { description, name: type } = roomType;
        const { name } = hotel;

        console.log(hotel, roomType);

        const roomSize =
          hotelAllotment.hotelAllotmentPricing?.find(ha => ha.allotmentPricingId === orderRow.hotelAllotmentPricingId)?.roomSize || roomType?.roomSize;
        const vatPercent =
          hotelAllotment.hotelAllotmentPricing?.find(ha => ha.allotmentPricingId === orderRow.hotelAllotmentPricingId)?.vatPercent || roomType?.vatPercent;

        if (!hotelAllotment) return acc;

        const index = acc.findIndex(a => a.hotelGroupId === orderRow.hotelGroupId);

        if (index !== -1) {
          acc[index].amount += orderRow.amount;
          acc[index].dates = [...acc[index].dates, hotelAllotment.date];
        } else {
          acc.push({
            ...orderRow,
            supplementId: hotelAllotment.supplementId,
            type,
            vatPercent,
            description,
            imageUrl: roomType.imageUrl || hotel.imageUrl || '',
            name: hotel.name,
            type: roomType.name,
            hotel: { name, description, roomSize },
            dates: [hotelAllotment.date],
          });
        }

        return acc;
      }, [])
      .map(({ dates, ...rest }) => {
        const dateValues = dates.map(d => +new Date(d.substring(0, 10)));

        const allotmentStartDate = Math.min(...dateValues);
        const allotmentEndDate = Math.max(...dateValues);

        const nights = hotelOrderRows.filter(or => or.hotelGroupId === rest.hotelGroupId)?.length;

        const checkinDate = new Date(allotmentStartDate).toISOString().substring(0, 10);
        const checkoutDate = new Date(new Date(allotmentEndDate).setDate(new Date(allotmentEndDate).getDate() + 1)).toISOString().substring(0, 10);

        const supplementPrice = breakouts?.find(b => b.eventId === rest.supplementId)?.amount ?? 0;
        const supplementVatPercent = breakouts?.find(b => b.eventId === rest.supplementId)?.vatPercent ?? 0;

        return {
          ...rest,
          nights,
          supplementPrice,
          supplementVatPercent,
          dateRangeString: formatDateRange(`${checkinDate} - ${checkoutDate}`, lang),
        };
      });

    return result;
  }, [hotelOrderRows, hotelAllotments, hotels, lang]);

  return { onChange: handleChange, bookings, handleFullBooked: setFullBooked, fullbooked, onDelete: handleDelete, onAdd: handleAdd };
}
